import { AlertTriangle, Check, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { UserStandard } from '@/shared/hooks/api/userStandards'
import { cn } from '@/shared/lib/utils'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  type ExtractedContentState,
  type ExtractedContentType,
  getExtractedContentTypeStates,
  hasHazardsOrIfuData,
  standardHasPdf,
} from '../utils'

const CONTENT_TYPE_LABEL_KEYS: Record<ExtractedContentType, string> = {
  scope: 'extractedInfo.contentType.scope',
  significantHazards: 'extractedInfo.contentType.significantHazards',
  informationForUse: 'extractedInfo.contentType.informationForUse',
  riskReductionMeasures: 'extractedInfo.contentType.riskReductionMeasures',
}

const STATE_LABEL_KEYS: Record<ExtractedContentState, string> = {
  filled: 'extractedInfo.state.filled',
  empty: 'extractedInfo.state.empty',
  notExtracted: 'extractedInfo.state.notExtracted',
  noPdf: 'extractedInfo.state.noPdf',
}

const STATE_TEXT_CLASS: Record<ExtractedContentState, string> = {
  filled: 'text-green-700 dark:text-green-100',
  empty: 'text-muted-foreground',
  notExtracted: 'text-blue-700 dark:text-blue-400',
  noPdf: 'text-amber-700 dark:text-amber-400',
}

function ExtractedInfoSummaryIcon({ standard }: { standard: UserStandard }) {
  const hasData = hasHazardsOrIfuData(standard)
  const hasPdf = standardHasPdf(standard)

  if (hasData) {
    return <Check className='size-4 text-green-600' aria-hidden />
  }
  if (hasPdf) {
    return <Info className='size-4 text-blue-600' aria-hidden />
  }
  return <AlertTriangle className='size-4 text-amber-500' aria-hidden />
}

export function ExtractedInfoStatusCell({
  standard,
}: {
  standard: UserStandard
}) {
  const { t } = useTranslation('standardsLibrary')
  const contentTypeStates = getExtractedContentTypeStates(standard)

  const hasData = hasHazardsOrIfuData(standard)
  const hasPdf = standardHasPdf(standard)

  let summaryLabel = t('dataStatusNoDataYet')
  if (hasData) {
    summaryLabel = t('dataStatusOk')
  } else if (hasPdf) {
    summaryLabel = t('dataStatusNotYetExtracted')
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={summaryLabel}
          onClick={(event) => event.stopPropagation()}
        >
          <ExtractedInfoSummaryIcon standard={standard} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
        <p className='text-sm font-medium'>{t('extractedInfo.hoverTitle')}</p>
        <ul className='mt-3 space-y-2'>
          {contentTypeStates.map(({ type, state }) => (
            <li
              key={type}
              className='flex items-start justify-between gap-3 text-sm'
            >
              <span className='text-popover-foreground'>
                {t(CONTENT_TYPE_LABEL_KEYS[type])}
              </span>
              <span
                className={cn(
                  'shrink-0 text-right font-medium',
                  STATE_TEXT_CLASS[state]
                )}
              >
                {t(STATE_LABEL_KEYS[state])}
              </span>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}
